import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { TxRaw } from 'cosmjs-types/cosmos/tx/v1beta1/tx'
import { assert } from '@/utils'
import { queryKeys } from '@/query-keys'
import { assertIsDeliverTxSuccess, broadcastTx } from '@/wallet-utils'
import { MutationParameters } from './types'
import { DeliverTxResponse } from '@cosmjs/stargate'
import { dismissedTransactionsAtom } from '../../atoms'
import { useStrideChain } from '@/contexts/wallet'

export interface BroadcastLiquidStakeMutationReturnType {
  transaction: DeliverTxResponse
}

const useBroadcastLiquidStakeMutation = ({
  ibcTransferTransaction,
  liquidStakeRaw,
  setLiquidStakeTransaction
}: MutationParameters) => {
  const { getSigningStargateClient } = useStrideChain()

  const queryClient = useQueryClient()

  const setDismissedTxs = useSetAtom(dismissedTransactionsAtom)

  const handleMutation = async (): Promise<BroadcastLiquidStakeMutationReturnType> => {
    assert(liquidStakeRaw, 'Unable to broadcast transaction without a signed transaction raw.')

    const client = await getSigningStargateClient()

    const bytes = TxRaw.encode(liquidStakeRaw).finish()

    const transaction = await broadcastTx(client, bytes)

    assertIsDeliverTxSuccess(transaction)

    return { transaction }
  }

  const handleSettled = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.transactionHistoryBase }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.chainBalancesBase
      })
    ])
  }

  const handleSuccess = async ({ transaction }: BroadcastLiquidStakeMutationReturnType) => {
    setLiquidStakeTransaction(transaction)

    if (ibcTransferTransaction) {
      // We'll check for the existence of the transaction ref since it's not guaranteed to exist
      // like for instance, user skipped the IBC transfer step.
      // We'll dismiss the preceding IBC transfer transaction since they're likely connected.
      // This would minimize the number of transactions in the transaction history.
      setDismissedTxs((dismissedTxs) => [...dismissedTxs, ibcTransferTransaction.transactionHash])
    }
  }

  return useMutation({
    mutationFn: handleMutation,
    onSettled: handleSettled,
    onSuccess: handleSuccess
  })
}

export { useBroadcastLiquidStakeMutation }
